import React, { useEffect, useRef, useState } from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import "./TrxNowGoogleMap.scss";

// available map markers
import DestMapMarker from "../../../assets/ui-img/map-markers/markerServiceLocation.png";
import TowMapMarker from "../../../assets/ui-img/map-markers/markerTow.png";
import stdMapMarker from "../../../assets/ui-img/map-markers/blueDot.png";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import LocationPinIcon from "@mui/icons-material/Place";
import { useTranslation } from "react-i18next";
import { devLog } from "utils/devLog";

//const stdMapMarker = 'data:image/svg+xml;utf-8, <svg width="60" height="60" xmlns="http://www.w3.org/2000/svg"><circle cx="30" cy="30" r="10" stroke="black" stroke-width="2" fill="lightblue" /><circle cx="30" cy="30" r="3" stroke="black" stroke-width="2" fill="blue" /></svg>';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || "";

const render = (status) => {
  if (status === Status.LOADING) return <h3>{status} ..</h3>;
  if (status === Status.FAILURE) return <h3>{status} ...</h3>;
  return null;
};

function GoogleMap({
  loading,
  showOverlay,
  setShowOverlay,
  disablementCoords, // = { lat: 36.317126, lng: -82.351134 },
  destinationCoords,
  zoom = 14,
  width,
  height,
  markersToShow,
  enRouteToService,
  onMarkerDragEnd,
  onZoomChange,
  onDisplayAutoCompleteList,
  markersDraggable = true,
}) {
  const { t } = useTranslation();
  const ref = useRef();
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const onZoomChangeRef = useRef(onZoomChange);

  // Overlay state for showing draggable pin message
  const hasShownOverlay = useRef(false);

  // State for closing animation
  const [isClosing, setIsClosing] = useState(false);
  const messageBoxRef = useRef(null);

  // Keep the callback ref updated
  useEffect(() => {
    onZoomChangeRef.current = onZoomChange;
  }, [onZoomChange]);

  // Prevent body scroll when overlay is shown
  useEffect(() => {
    if (showOverlay) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
    };
  }, [showOverlay]);

  // Initialize map only once
  useEffect(() => {
    if (!mapRef.current && ref.current) {
      devLog("Initialize Map - Disablement Coords! -----", disablementCoords?.lat, disablementCoords?.lng);
      let map = new window.google.maps.Map(ref.current, {
        center: disablementCoords?.lat ? disablementCoords : { lat: 39.8283, lng: -98.5795 },
        zoom: disablementCoords?.lat ? zoom : 3.8,
        gestureHandling: "greedy",
        disableDefaultUI: true,
        zoomControl: true,
        // Removed scrollWheelZoom: "center" to allow normal zoom behavior
      });

      mapRef.current = map;

      map.addListener("zoom_changed", function () {
        var currZoom = map.getZoom();
        devLog("Zoom changed: ", currZoom);
        if (onZoomChangeRef.current) {
          onZoomChangeRef.current(currZoom);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  // Update center when disablementCoords changes (not on zoom changes)
  useEffect(() => {
    if (mapRef.current && disablementCoords?.lat) {
      const currentCenter = mapRef.current.getCenter();
      const newLat = disablementCoords.lat;
      const newLng = disablementCoords.lng;

      // Only update center if coordinates actually changed
      if (
        !currentCenter ||
        Math.abs(currentCenter.lat() - newLat) > 0.0001 ||
        Math.abs(currentCenter.lng() - newLng) > 0.0001
      ) {
        devLog("Updating map center to:", newLat, newLng);
        mapRef.current.setCenter(disablementCoords);
        // Set zoom to 14 when current location is obtained
        mapRef.current.setZoom(zoom);
      }
    }
  }, [disablementCoords]);

  // Show overlay when location first becomes available
  useEffect(() => {
    if (disablementCoords?.lat && !hasShownOverlay.current && setShowOverlay) {
      hasShownOverlay.current = true;
      setShowOverlay(true);

      // Hide overlay after 2 seconds
      // const timer = setTimeout(() => {
      //   setShowOverlay(false);
      // }, 3000);

      // return () => clearTimeout(timer);
    }
  }, [disablementCoords, setShowOverlay]);

  // Handle close with suck-in animation
  const handleCloseOverlay = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually hiding
    setTimeout(() => {
      setIsClosing(false);
      setShowOverlay(false);
    }, 400); // Match animation duration
  };

  // Clear and recreate markers when coordinates or markers change
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    // create disablement & dest markers if they exist
    if (disablementCoords && disablementCoords.lat !== undefined) {
      if (disablementCoords.lat !== 0) {
        let primaryMarkers = [
          {
            position: disablementCoords,
            icon: enRouteToService ? TowMapMarker : <LocationPinIcon style={{ color: "red" }} />,
            draggable: markersDraggable,
            isPrimary: true, // Flag to identify the primary marker for drag handling
          },
        ];

        // if dest coords provided show marker
        if (
          destinationCoords &&
          destinationCoords.lat !== undefined &&
          destinationCoords.lat !== 0 &&
          destinationCoords.lng !== undefined
        ) {
          primaryMarkers.push({
            position: destinationCoords,
            icon: enRouteToService ? DestMapMarker : TowMapMarker,
            draggable: markersDraggable,
          });
        } else {
          devLog("Invalid dest coords:", destinationCoords);
        }

        devLog("primaryMarkers length: ", primaryMarkers.length);

        const createdMarkers = CreateMapMarkers(
          primaryMarkers,
          mapRef.current,
          onMarkerDragEnd,
          onDisplayAutoCompleteList,
        );
        if (createdMarkers) {
          markersRef.current.push(...createdMarkers);
        }
      }
    }

    // show any aditional map markers
    if (markersToShow && markersToShow.length > 0) {
      const createdMarkers = CreateMapMarkers(
        markersToShow,
        mapRef.current,
        onMarkerDragEnd,
        onDisplayAutoCompleteList,
      );
      if (createdMarkers) {
        markersRef.current.push(...createdMarkers);
      }
    }
  }, [disablementCoords, destinationCoords, markersToShow, enRouteToService, onMarkerDragEnd, markersDraggable]);

  return (
    <div style={{ position: "relative", width: width, height: height }}>
      <div style={{ width: "100%", height: "100%" }} ref={ref} id="map" />

      {/* Overlay message for draggable pin */}
      {!loading && showOverlay && (
        <div
          className="overlay-message-box"
          onClick={handleCloseOverlay}
          style={{
            backgroundColor: isClosing ? "rgba(0, 0, 0, 0)" : "rgba(0, 0, 0, 0.5)",
          }}
        >
          <div
            className="overlay-message-box-content"
            ref={messageBoxRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              boxShadow: isClosing ? "0 2px 10px rgba(0, 0, 0, 0.2)" : "0 4px 20px rgba(0, 0, 0, 0.3)",

              animation: isClosing
                ? "suckIn 0.4s cubic-bezier(0.55, 0.085, 0.68, 0.53) forwards"
                : "fadeInScale 0.3s ease-out",
            }}
          >
            <button
              className="overlay-message-box-close-button"
              onClick={handleCloseOverlay}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#f0f0f0")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              <CloseIcon style={{ fontSize: "20px", color: "#666" }} />
            </button>
            <div className="overlay-message">
              <LocationOnIcon style={{ fontSize: "32px", color: "red" }} />
              <div className="overlay-message-box-content-text">{t("description.DragPinHelpMsg")}</div>
            </div>
            <div className="overlay-message">
              <MyLocationIcon style={{ fontSize: "32px" }} />
              <div className="overlay-message-box-content-text">{t("description.ReturnToOriginalLocationHelpMsg")}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TrxNowGoogleMap({
  loading,
  showOverlay,
  setShowOverlay,
  disablementCoords, // = { lat: 36.317126, lng: -82.351134 },
  destinationCoords, // = { lat: 36.317126, lng: -82.359934 },
  zoom = 14,
  width = "100%",
  onZoomChange,
  height = window.innerHeight,
  markers,
  enRouteToService,
  onMarkerDragEnd,
  onDisplayAutoCompleteList,
  markersDraggable = true,
}) {
  devLog("disab coords", disablementCoords);
  devLog("dest coords", destinationCoords);
  devLog("zoom:", zoom);
  devLog("Width:", width);
  devLog("Height:", height);
  devLog("Markers:", markers);
  return (
    <Wrapper apiKey={GOOGLE_API_KEY} render={render}>
      <GoogleMap
        loading={loading}
        showOverlay={showOverlay}
        setShowOverlay={setShowOverlay}
        disablementCoords={disablementCoords}
        destinationCoords={destinationCoords}
        zoom={zoom}
        width={width}
        height={height}
        markersToShow={markers}
        enRouteToService={enRouteToService}
        onMarkerDragEnd={onMarkerDragEnd}
        onZoomChange={onZoomChange}
        onDisplayAutoCompleteList={onDisplayAutoCompleteList}
        markersDraggable={markersDraggable}
      />
    </Wrapper>
  );
}

function CreateMapMarkers(mapMarkers, map, onMarkerDragEnd, onDisplayAutoCompleteList) {
  let markersList = [];
  const bounds = new window.google.maps.LatLngBounds();

  devLog("mapMakers:", mapMarkers);

  if (!mapMarkers || mapMarkers === undefined || mapMarkers.length < 1) {
    return;
  }

  mapMarkers.map((marker) => {
    devLog("Marker:", marker);
    devLog("marker.position:", marker.position);

    const location = new window.google.maps.LatLng(marker.position.lat, marker.position.lng);
    bounds.extend(location);

    // create the marker
    const newMarker = new window.google.maps.Marker({
      position: marker.position,
      draggable: marker.draggable || false,
      icon: marker.icon || null,
      map: map,
    });

    // set marker options (excluding draggable since it's already set above)
    const { draggable, isPrimary, ...otherOptions } = marker;
    newMarker.setOptions(otherOptions);

    // add dragstart event listener for primary marker
    if (marker.isPrimary) {
      newMarker.addListener("dragstart", () => {
        devLog("Pin drag started");
        if (typeof onDisplayAutoCompleteList === "function") {
          devLog("onDisplayAutoCompleteList is a function");
          onDisplayAutoCompleteList(false);
          devLog("onDisplayAutoCompleteList set to false");
        }
      });
    }

    // add dragend event listener for primary marker
    if (marker.isPrimary && onMarkerDragEnd) {
      newMarker.addListener("dragend", (event) => {
        const newPosition = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };
        devLog("Marker dragged to:", newPosition);
        onMarkerDragEnd(newPosition);

        // Show autocomplete list again when dragging stops
        if (typeof onDisplayAutoCompleteList === "function") {
          devLog("Releasing the dragging");
          onDisplayAutoCompleteList(true);
        }
      });
    }

    // add to the list
    markersList.push(newMarker);

    return null;
  });

  // only fit to bounds if more than one bound is set
  // otherwise it ignores zoom level
  if (mapMarkers.length > 1) {
    devLog("Calling fit to bounds");
    map.fitBounds(bounds, 100);
  } else {
    devLog("Not calling fit to bounds");
  }

  return markersList;
}
